import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Unit, Lesson } from '../../hooks/useCourseApi';
import { CONTENT_MESSAGES } from '../../utils/contentMessages';

interface CurriculumProps {
  units: Unit[];
  courseId: string;
  onLessonSelect?: (lesson: Lesson, unit: Unit) => void;
  onUnitClick?: (unit: Unit) => void;
}

const LessonItem: React.FC<{
  lesson: Lesson;
  lessonIndex: number;
  unit: Unit;
  onSelect: (lesson: Lesson, unit: Unit) => void;
}> = ({ lesson, lessonIndex, unit, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(lesson, unit)}
    className="flex w-full items-center gap-3 border-t border-sage-100 px-4 py-3 text-right transition hover:bg-sage-50"
  >
    <span className="w-6 shrink-0 text-xs text-slate-400">{lessonIndex + 1}</span>
    <div className="min-w-0 flex-1">
      <h4 className="truncate text-sm text-slate-800">{lesson.lessonName}</h4>
      <p className="text-xs text-slate-400">{lesson.type === 'Video' ? 'فيديو' : lesson.type}</p>
    </div>
  </button>
);

const UnitAccordion: React.FC<{
  unit: Unit;
  unitIndex: number;
  isExpanded: boolean;
  onToggle: () => void;
  onSelectLesson: (lesson: Lesson, unit: Unit) => void;
  onUnitClick?: (unit: Unit) => void;
}> = ({ unit, unitIndex, isExpanded, onToggle, onSelectLesson, onUnitClick }) => (
  <div className="border-b border-sage-200 last:border-b-0">
    <div className="flex items-center gap-2 py-3">
      <button
        type="button"
        onClick={() => (onUnitClick ? onUnitClick(unit) : onToggle())}
        className="min-w-0 flex-1 py-1 text-right"
      >
        <span className="block truncate text-sm font-semibold text-slate-900 sm:text-base">
          {unitIndex + 1}. {unit.unitName}
        </span>
        <span className="text-xs text-slate-400">{unit.lessons.length} دروس</span>
      </button>
      <button
        type="button"
        onClick={onToggle}
        className="rounded-lg p-2 text-slate-400 hover:bg-sage-50 hover:text-slate-600"
        aria-expanded={isExpanded}
        aria-label={isExpanded ? 'طي الوحدة' : 'توسيع الوحدة'}
      >
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
    </div>

    {isExpanded && (
      <div className="pb-2">
        {unit.lessons.map((lesson, lessonIndex) => (
          <LessonItem
            key={lesson.id}
            lesson={lesson}
            lessonIndex={lessonIndex}
            unit={unit}
            onSelect={onSelectLesson}
          />
        ))}
      </div>
    )}
  </div>
);

export const Curriculum: React.FC<CurriculumProps> = ({
  units,
  onLessonSelect,
  onUnitClick,
}) => {
  const [expandedUnits, setExpandedUnits] = useState<number[]>(units[0] ? [units[0].id] : []);

  const toggleUnit = (unitId: number) => {
    setExpandedUnits((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );
  };

  if (!units.length) {
    return <p className="text-sm text-slate-500">{CONTENT_MESSAGES.noUnits}</p>;
  }

  return (
    <div className="rounded-2xl border border-sage-200 bg-white px-2 sm:px-3">
      {units.map((unit, unitIndex) => (
        <UnitAccordion
          key={unit.id}
          unit={unit}
          unitIndex={unitIndex}
          isExpanded={expandedUnits.includes(unit.id)}
          onToggle={() => toggleUnit(unit.id)}
          onSelectLesson={(lesson, u) => onLessonSelect?.(lesson, u)}
          onUnitClick={onUnitClick}
        />
      ))}
    </div>
  );
};
